/* Task 7. Sizes use the existing Shopify picker; this component never sets prices or cart data. */
(() => {
  if (customElements.get('mc-room-guide')) return;
  const dimensions = value => String(value || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i)?.slice(1).map(Number);
  let hintDismissed = false;
  class RoomGuide extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.gallery = this.gallery || this.closest('media-gallery');
      this.productInfo = this.productInfo || this.closest('product-info');
      if (!this.gallery || !this.productInfo) return;
      this.setHintDismissed(hintDismissed);
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.querySelector('[data-room-open]')?.addEventListener('click', event => {
        document.getElementById('ProductModal-' + this.productInfo.dataset.section)?.show(event.currentTarget);
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-room-size]');
        if (!button) return;
        this.gallery.dataset.currentMedia = this.closest('[data-media-id]').dataset.mediaId;
        const control = this.controls().find(input => input.value === button.dataset.roomSize);
        if (!control || control.disabled) return;
        hintDismissed = true;
        this.setHintDismissed(true);
        // Native input change also runs Dawn's abortable variant request.
        if (control.tagName === 'OPTION') {
          control.parentElement.value = control.value;
          control.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else control.click();
        this.sync();
      }, options);
      this.productInfo.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) this.sync();
      }, options);
      this.productInfo.addEventListener('mc:picker-ready', () => this.sync(), options);
      this.visibility = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadImages();
          this.visibility.disconnect();
        }
      });
      if (!this.enlarged) this.visibility.observe(this.gallery);
      this.artworkResize = new ResizeObserver(() => this.renderArtwork());
      this.scenes().forEach(scene => this.artworkResize.observe(scene));
      window.addEventListener('resize', () => this.renderArtwork(), options);
      this.sync();
      if (this.enlarged) this.loadImages();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.artworkResize?.disconnect();
      this.visibility?.disconnect();
    }
    setHintDismissed(dismissed) {
      const hint = this.querySelector('[data-room-hint]');
      if (!hint) return;
      hint.classList.toggle('is-dismissed', dismissed);
      hint.setAttribute('aria-hidden', String(dismissed));
    }
    controls() {
      // Dawn retains the hidden outgoing picker briefly during its DOM swap.
      const picker = [...this.productInfo.querySelectorAll('variant-selects')]
        .find(element => !element.hidden && getComputedStyle(element).display !== 'none');
      if (!picker) return [];
      const option = picker.querySelector('[data-mc-option-position="' + this.dataset.sizePosition + '"]');
      return [...(option?.querySelectorAll('input[type="radio"], option') || [])];
    }
    scenes() {
      return this.enlarged ? [this.querySelector('[data-room-scene]')] : [...this.gallery.querySelectorAll('[data-room-scene]')];
    }
    safeArtworkUrl(value) {
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && !/upscaled|wrap_/i.test(decodeURIComponent(url.href));
      } catch { return false; }
    }
    responsiveArtworkSizes() {
      // Read the calibrated artwork box, including size selection and desktop pixel snapping.
      // Before the slide opens, its visible thumbnail supplies a small initial request.
      const width = this.querySelector('[data-room-art]').getBoundingClientRect().width ||
        this.scenes().map(scene => scene.querySelector('[data-room-art]').getBoundingClientRect().width).find(value => value > 0);
      return Number(Math.max(1, width || 1).toFixed(3)) + 'px';
    }
    updateResponsiveArtwork() {
      const image = this.responsiveArtwork;
      if (!image) return;
      const sizes = this.responsiveArtworkSizes();
      const width = parseFloat(sizes);
      const inRange = this.responsiveSources.filter(source => source.width >= width * (this.enlarged ? Math.max(1.5, window.devicePixelRatio || 1) : 1.5) && source.width <= width * 2.5);
      // When no tier meets the ratio band, enlarged views must still cover device pixels.
      const resolutionSafe = this.enlarged ? this.responsiveSources.filter(source => source.width >= width * (window.devicePixelRatio || 1)) : this.responsiveSources;
      const candidates = inRange.length ? inRange : resolutionSafe.length ? resolutionSafe : this.responsiveSources;
      const selected = candidates.reduce((best, source) =>
        Math.abs(source.width - width * 2) < Math.abs(best.width - width * 2) ? source : best);
      const source = this.querySelector('[data-room-art-source]');
      const srcset = selected.url + ' ' + selected.width + 'w';
      if (image.sizes !== sizes) image.sizes = sizes;
      if (source.sizes !== sizes) source.sizes = sizes;
      if (source.srcset !== srcset) source.srcset = srcset;
      this.dataset.webSelectedWidth = selected.width;
      this.dataset.webRatioLimited = String(!inRange.length);
    }
    loadResponsiveArtwork() {
      const sources = [320, 480, 640, 800, 1200, 1600].map(width => ({
        width, url: this.dataset['responsiveWeb-' + width]?.trim()
      }));
      if (!sources.every(source => this.safeArtworkUrl(source.url))) return false;
      const small = sources[0].url;
      const image = this.querySelector('[data-room-art-image]');
      this.responsiveArtwork = image;
      this.responsiveSources = sources;
      image.dataset.roomResponsive = 'true';
      image.loading = 'eager';
      image.onload = () => {
        if (!this.isConnected || this.responsiveArtwork !== image) return;
        this.dataset.sourceTier = 'web-responsive';
        this.dataset.artworkUrl = image.currentSrc;
        this.dataset.orientation = image.naturalWidth > image.naturalHeight ? 'landscape' : 'portrait';
        this.dataset.naturalWidth = image.naturalWidth;
        this.dataset.naturalHeight = image.naturalHeight;
        this.scenes().forEach(scene => {
          const target = scene.querySelector('[data-room-art-image]');
          if (target !== image && target.src !== image.currentSrc) target.src = image.currentSrc;
          target.hidden = false;
          scene.querySelector('[data-room-placeholder]').hidden = true;
        });
        this.sync();
      };
      image.onerror = () => {
        if (!this.isConnected || this.responsiveArtwork !== image) return;
        this.dataset.webSourceFailed = image.currentSrc || small;
        this.responsiveArtwork = null;
        image.onload = null;
        image.onerror = null;
        const source = this.querySelector('[data-room-art-source]');
        source.removeAttribute('srcset');
        source.removeAttribute('sizes');
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        delete image.dataset.roomResponsive;
        this.scenes().forEach(scene => {
          scene.querySelector('[data-room-art-image]').hidden = true;
          scene.querySelector('[data-room-placeholder]').hidden = false;
        });
        this.loadArtworkFallback();
      };
      this.updateResponsiveArtwork();
      image.srcset = sources.map(source => source.url + ' ' + source.width + 'w').join(', ');
      image.src = small;
      return true;
    }
    loadImages() {
      this.dataset.imagesRequested = 'true';
      this.scenes().forEach(scene => {
        const background = scene.querySelector('[data-room-src]');
        const source = scene.querySelector('[data-room-srcset]');
        background.loading = 'eager';
        source.srcset = source.dataset.roomSrcset;
        background.src = background.dataset.roomSrc;
        background.addEventListener('error', () => {
          source.removeAttribute('srcset');
          background.src = background.dataset.roomSrc;
        }, { once: true, signal: this.abort.signal });
      });
      this.gallery.refreshImageSizes?.();
      if (!this.loadResponsiveArtwork()) this.loadArtworkFallback();
    }
    loadArtworkFallback() {
      const candidates = [
        ['web-metafield', this.dataset.webUrl],
        ['processed', this.dataset.processedUrl],
        ['featured-media', this.dataset.featuredUrl]
      ].filter(([, value]) => this.safeArtworkUrl(value));
      const loadNext = () => {
        if (!this.isConnected) return;
        const next = candidates.shift();
        if (!next) {
          this.dataset.sourceTier = 'placeholder';
          this.dataset.artworkUrl = '';
          return;
        }
        this.dataset.sourceTier = next[0];
        this.dataset.artworkUrl = next[1];
        this.artworkPreload.src = next[1];
      };
      this.artworkPreload = new Image();
      this.artworkPreload.onload = () => {
        if (!this.isConnected) return;
        this.dataset.orientation = this.artworkPreload.naturalWidth > this.artworkPreload.naturalHeight ? 'landscape' : 'portrait';
        this.dataset.naturalWidth = this.artworkPreload.naturalWidth;
        this.dataset.naturalHeight = this.artworkPreload.naturalHeight;
        this.scenes().forEach(scene => {
          const image = scene.querySelector('[data-room-art-image]');
          const artwork = new URL(this.artworkPreload.src);
          const shopifyImage = artwork.hostname === 'cdn.shopify.com' ||
            (artwork.origin === location.origin && artwork.pathname.startsWith('/cdn/shop/'));
          if (shopifyImage) {
            const originalWidth = this.artworkPreload.naturalWidth;
            const widths = [...new Set([64, 96, 128, 192, 256, 384, 512, 768, 960, 1200, originalWidth])]
              .filter(width => width <= originalWidth).sort((a, b) => a - b);
            image.srcset = widths.map(width => {
              const url = new URL(artwork); url.searchParams.set('width', width);
              return url.href + ' ' + width + 'w';
            }).join(', ');
          }
          image.src = this.artworkPreload.src;
          image.hidden = false;
          scene.querySelector('[data-room-placeholder]').hidden = true;
        });
        this.sync();
      };
      this.artworkPreload.onerror = loadNext;
      loadNext();
    }
    syncButtons(controls) {
      const strip = this.querySelector('[data-room-sizes]');
      const focusedValue = strip.contains(document.activeElement) ? document.activeElement.dataset.roomSize : null;
      strip.replaceChildren(...controls.map(control => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.roomSize = control.value;
        button.textContent = control.value;
        return button;
      }));
      if (focusedValue) [...strip.children].find(button => button.dataset.roomSize === focusedValue)?.focus({ preventScroll: true });
    }
    sync() {
      const controls = this.controls();
      this.syncButtons(controls);
      const selected = controls.find(input => input.checked || input.selected);
      this.querySelectorAll('[data-room-size]').forEach(button => {
        const control = controls.find(input => input.value === button.dataset.roomSize);
        button.disabled = !control || control.disabled;
        const unavailable = !control || control.classList.contains('disabled') || control.disabled;
        button.classList.toggle('is-unavailable', unavailable);
        button.setAttribute('aria-label', button.dataset.roomSize + (unavailable ? ' — sold out or unavailable' : ''));
        button.setAttribute('aria-pressed', String(control === selected));
      });
      if (!selected) return;
      this.dataset.selectedSize = selected.value;
      this.renderArtwork();
      this.querySelector('[data-room-status]').textContent = selected.value + ' selected';
      const pressed = this.querySelector('[aria-pressed="true"]');
      const strip = this.querySelector('[data-room-sizes]');
      if (pressed && strip) strip.scrollTo({ left: pressed.offsetLeft - strip.offsetLeft - (strip.clientWidth - pressed.offsetWidth) / 2 });
    }
    artworkFocus() {
      const match = String(this.dataset.artworkFocus || '').trim().match(/^(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
      if (!match || match.slice(1).some(value => Number(value) > 100)) return '50% 50%';
      return Number(match[1]) + '% ' + Number(match[2]) + '%';
    }
    renderArtwork() {
      const size = dimensions(this.dataset.selectedSize);
      if (!size) return;
      let [width, height] = size;
      if (this.dataset.orientation === 'landscape') [width, height] = [Math.max(...size), Math.min(...size)];
      else [width, height] = [Math.min(...size), Math.max(...size)];
      const scale = Number(this.dataset.referencePercent) / Number(this.dataset.referenceInches);
      if (this.enlarged) {
        const touch = window.matchMedia('(max-width: 1023.98px)').matches && navigator.maxTouchPoints > 0;
        const largest = Math.max(...(this.responsiveSources || [{ width: 1600 }]).map(source => source.width));
        this.style.setProperty('--mc-room-zoom-max-width', touch ?
          largest / (4 * (window.devicePixelRatio || 1) * width * scale / 100) + 'px' : '100000px');
      }
      const furniture = Number(this.dataset.furnitureY);
      const floor = Number(this.dataset.floorY);
      // This square scene uses image-width percentages on both axes.
      // Clamp the 63-inch centre to the permitted 10–24-inch furniture clearance.
      const center = Math.min(furniture - 10 * scale - height * scale / 2,
        Math.max(furniture - 24 * scale - height * scale / 2, floor - 63 * scale));
      this.scenes().forEach(scene => {
        const art = scene.querySelector('[data-room-art]');
        scene.querySelector('[data-room-art-image]').style.objectPosition = this.artworkFocus();
        const top = center - height * scale / 2;
        const centerX = Number(this.dataset.centerX);
        if (window.matchMedia('(min-width: 1024px)').matches && scene.closest('mc-room-guide')) {
          // Align the actual rectangle to physical pixels; do not scale a layer.
          const rect = scene.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          const snap = value => Math.round(value * dpr) / dpr;
          art.style.transform = 'none';
          art.style.width = snap(rect.width * width * scale / 100) + 'px';
          art.style.height = snap(rect.width * height * scale / 100) + 'px';
          art.style.left = snap(rect.left + rect.width * (centerX - width * scale / 2) / 100) - rect.left + 'px';
          art.style.top = snap(rect.top + rect.width * top / 100) - rect.top + 'px';
        } else {
          art.style.removeProperty('transform');
          art.style.width = width * scale + '%';
          art.style.height = height * scale + '%';
          art.style.left = centerX + '%';
          art.style.top = top + '%';
        }
        if (this.enlarged) {
          const background = scene.querySelector('[data-room-src]');
          background.sizes = Number(scene.getBoundingClientRect().width.toFixed(3)) + 'px';
        }
        scene.style.setProperty('--mc-room-art-bottom', center + height * scale / 2 + '%');
      });
      this.updateResponsiveArtwork();
      this.gallery.refreshImageSizes?.();
      this.dataset.clearanceInches = (furniture - center - height * scale / 2) / scale;
      this.dataset.centerInches = (floor - center) / scale;
    }
  }
  customElements.define('mc-room-guide', RoomGuide);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    hintDismissed = false;
    document.querySelectorAll('mc-room-guide').forEach(guide => guide.setHintDismissed(false));
  });
})();
